import { onBeforeUnmount, onMounted, ref, Ref, watch } from 'vue'
import { useDashboardContext } from './dashboardContext'

interface Options {
  /** Период автообновления, мс. 0: без автообновления. */
  intervalMs?: number
}

/**
 * Данные виджета: загрузка, ошибка, автообновление.
 * - при ошибке прежние данные остаются на экране, а виджет показывает сообщение и кнопку «Повторить»;
 * - в фоновой вкладке не обновляется, при возврате на вкладку обновляется сразу;
 * - устаревшие ответы отбрасываются.
 */
export function useWidgetData<T>(fetcher: () => Promise<T>, options: Options = {}) {
  const intervalMs = options.intervalMs === undefined ? 60000 : options.intervalMs
  const context = useDashboardContext()

  const data = ref(null) as Ref<T | null>
  const loading = ref(false)
  const error = ref<string | null>(null)
  const updatedAt = ref<Date | null>(null)
  let requestId = 0
  let timer: number | undefined

  const refresh = async () => {
    const current = ++requestId
    loading.value = true
    try {
      const result = await fetcher()
      if (current === requestId) {
        data.value = result
        error.value = null
        updatedAt.value = new Date()
      }
    } catch (e) {
      if (current === requestId) {
        error.value = typeof e === 'string' ? e : 'Не удалось загрузить данные'
      }
    } finally {
      if (current === requestId) {
        loading.value = false
      }
    }
  }

  const onVisible = () => {
    if (!document.hidden) {
      refresh()
    }
  }

  onMounted(() => {
    refresh()
    if (intervalMs > 0) {
      timer = window.setInterval(() => {
        if (!document.hidden) {
          refresh()
        }
      }, intervalMs)
      document.addEventListener('visibilitychange', onVisible)
    }
  })

  onBeforeUnmount(() => {
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  })

  watch(context.refreshTick, refresh)

  return { data, loading, error, updatedAt, refresh }
}
